import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import toastr from "toastr";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import LinearProgress from "@material-ui/core/LinearProgress";
import Fab from "@material-ui/core/Fab";
import Divider from "@material-ui/core/Divider";

import DescriptionIcon from "@material-ui/icons/Description";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";
import Button from "components/CustomButtons/Button";

import { actionCreators as actionCreatorsAdmin } from "../../../../_actions/adminActions";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  marginTop: {
    marginTop: theme.spacing(3)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  fullWidth: {
    width: "100%"
  },
  fileUpload: {
    display: "none"
  },
  cursorPointer: {
    cursor: "pointer"
  }
}));

const ChapterBudgetForm = ({
  isUpdating,
  getChapterBudget,
  activeChapterBudget,
  isFetching,
  isFetched,
  createChapterBudget,
  updateChapterBudget,
  updateChapterBudgetFile,
  downloadChapterBudgetFile
}) => {
  const classes = useStyles();
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [budget, setBudget] = useState(null);
  const [notes, setNotes] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const getQuery = useCallback(() => {
    return new URLSearchParams(location.search);
  }, [location]);

  useEffect(() => {
    if (id !== "add") {
      if (!isFetching.CHAPTER_BUDGET && !isFetched.CHAPTER_BUDGET) {
        getChapterBudget(id);
      }
    }
  }, [id]);

  useEffect(() => {
    if (activeChapterBudget) {
      setBudget(activeChapterBudget);
      setNotes(activeChapterBudget.notes || "");
    }
  }, [activeChapterBudget]);

  const handleNoteChange = useCallback(({ target: { value } }) => {
    setNotes(value);
  }, []);

  const handleFileChange = useCallback(({ target: { files } }) => {
    setSelectedFile(files[0]);
  }, []);

  const handleSave = useCallback(async () => {
    if (id === "add") {
      try {
        if (!selectedFile) {
          throw new Error("No File selected");
        }
        const queryString = getQuery();
        const result = await createChapterBudget({
          chapter: {
            id: queryString.get("chapter")
          },
          year: queryString.get("year"),
          notes
        });
        if (result.isSuccess) {
          toastr.success("Budget created successfully");
        }
        const formData = new FormData();
        formData.append("file", selectedFile, selectedFile.name);
        const fileResult = await updateChapterBudgetFile(
          result.item.id,
          formData
        );
        if (fileResult === true) {
          toastr.success("File uploaded successfully");
        }
        history.push("/admin/settings/chapter-budgets");
      } catch (err) {
        toastr.error(err);
      }
    } else {
      try {
        const result = await updateChapterBudget({
          id,
          notes,
          chapter: {
            id: budget.chapter.id
          },
          year: budget.year
        });
        if (result === true) {
          toastr.success("Budget updated successfully");
        }
        if (selectedFile) {
          const formData = new FormData();
          formData.append("file", selectedFile, selectedFile.name);
          const fileResult = await updateChapterBudgetFile(id, formData);
          if (fileResult === true) {
            toastr.success("File uploaded successfully");
          }
        }
        history.push("/admin/settings/chapter-budgets");
      } catch (err) {
        toastr.error(err);
      }
    }
  }, [id, notes, selectedFile, getQuery, history, budget]);

  const getFiscalYear = useCallback(() => {
    return id === "add" ? getQuery().get("year") : budget && budget.year;
  }, [id, getQuery, budget]);

  const handleCancel = useCallback(() => {
    history.push("/admin/settings/chapter-budgets");
  }, [history]);

  const handleDownloadFile = useCallback(
    filename => () => {
      if (id !== "add" && budget) {
        downloadChapterBudgetFile(budget.chapter.id, filename);
      }
    },
    [id, budget, downloadChapterBudgetFile]
  );

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Budget -{" "}
              {id === "add"
                ? getQuery().get("chapterName")
                : budget && budget.chapter.name}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Paper className={classes.paper}>
            <Grid container justify="center">
              <Grid item xs={12} md={6}>
                <Typography
                  className={clsx(classes.marginBottom, classes.marginTop)}
                >
                  For FY {getFiscalYear()}
                </Typography>
                <Typography className={classes.marginBottom}>
                  <b>Budget File</b>
                </Typography>

                <Grid
                  container
                  direction="column"
                  className={classes.fullWidth}
                >
                  {id !== "add" && budget && (
                    <Grid item xs={12}>
                      <b>Current File:</b>{" "}
                      <a
                        className={classes.cursorPointer}
                        onClick={handleDownloadFile(budget.filename)}
                      >
                        {budget.filename}
                      </a>
                    </Grid>
                  )}
                  <Grid
                    container
                    justify="space-between"
                    alignItems="center"
                    className={classes.marginBottom}
                  >
                    <span>
                      <b>{id === "add" ? "File" : "Replace with"}:</b>{" "}
                      {selectedFile ? selectedFile.name : "No file chosen"}
                    </span>
                    <div>
                      <input
                        className={classes.fileUpload}
                        type="file"
                        accept=".xls,.xlsx,.doc,.docx,.pdf"
                        id="upload-file-button"
                        onChange={handleFileChange}
                      />
                      <label
                        htmlFor="upload-file-button"
                        style={{ marginLeft: "20px" }}
                      >
                        <Fab component="span" size="small">
                          <DescriptionIcon />
                        </Fab>
                      </label>
                    </div>
                  </Grid>
                  <Grid item className={classes.marginBottom}>
                    (PDF, MS Word or MS Excel Only)
                  </Grid>
                  <Grid item xs={12} className={classes.marginBottom}>
                    <TextField
                      label="Notes"
                      multiline
                      rows={5}
                      value={notes}
                      onChange={handleNoteChange}
                      variant="outlined"
                      className={classes.fullWidth}
                    />
                  </Grid>
                  {(isUpdating.CHAPTER_BUDGET ||
                    isUpdating.CHAPTER_BUDGET_FILE) && (
                    <Grid item xs={12}>
                      <LinearProgress />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid container justify="space-between">
                      <Button
                        color="success"
                        variant="contained"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                      <Button
                        color="danger"
                        variant="contained"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  isUpdating: state.adminReducer.isUpdating,
  isFetching: state.adminReducer.isFetching,
  isFetched: state.adminReducer.isFetched,
  activeChapterBudget: state.adminReducer.activeChapterBudget
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChapterBudgetForm);
